import { Flex, Input, Steps, Text, AppLoader, ChoiceInput } from '@components';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTheme } from '@mui/material';
import { registerInput } from '@utils/reactHookForm';
import { getErrorProps } from '@utils/validation/validationFunctions';
import { companyAuthorizedSignatoriesFormSchema } from '@validationSchemas/investment';
import WithUser from '@HOCs/withUser';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import InvestmentFooter from '../InvestmentFooter/InvestmentFooter';
import { ContentContainer } from './CompanyAuthorizedSignatories.styles';
import { Steps as ProgressSteps } from '@pages/Investment/Progress/Progress.types';
import { WithUserProps } from 'HOCs/withUser';
import { useEffect } from 'react';
import { InvestmentContainer } from '../Investment.styles';
import { LEGAL_ENTITY_TOTAL_STEPS } from '../Investment.consts';
import { yesNoChoices } from '@utils/choiceValues/choiceValues';
import { CompanyAuthorizedSignatoriesFormData } from './CompanyAuthorizedSignatories.types';
import { useCompanyVerificationRequestMutation } from '@features/api/companySlice';
import {
  useCompanyAuthorizedSignatoriesQuery,
  useCreateAuthorizedSignatoryMutation,
  useDeleteAuthorizedSignatoryMutation,
  useUpdateAuthorizedSignatoryMutation,
} from '@features/api/companySignatoriesSlice';
import { CompanyAuthorizedSignatory } from '@features/api/response/CompanyAuthorizedSignatory';

const CompanyAuthorizedSignatories: React.FC<
  WithUserProps & {
    signatories: CompanyAuthorizedSignatory[];
  }
> = ({ user, signatories }) => {
  const theme = useTheme();
  const intl = useIntl();
  const navigate = useNavigate();
  const { productId } = useParams();

  const [
    sendCompanyVerificationRequestMutation,
    { isLoading: isVerificationRequestLoading },
  ] = useCompanyVerificationRequestMutation();

  const [createMutation] = useCreateAuthorizedSignatoryMutation();
  const [updateMutation] = useUpdateAuthorizedSignatoryMutation();
  const [deleteMutation] = useDeleteAuthorizedSignatoryMutation();

  const defaultSignatory = signatories.find((x) => x.isPrimary);
  const additionalSignatory = signatories.find((x) => !x.isPrimary);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isValid },
    watch,
    setValue,
  } = useForm<CompanyAuthorizedSignatoriesFormData>({
    resolver: yupResolver(companyAuthorizedSignatoriesFormSchema),
    mode: 'onChange',
    shouldFocusError: false,
    defaultValues: signatories
      ? {
          title: defaultSignatory?.title,
          isThereAdditionalSignatory: !!additionalSignatory,
          additionalSignatory: additionalSignatory
            ? {
                firstName: additionalSignatory.firstName,
                middleName: additionalSignatory.middleName,
                lastName: additionalSignatory.lastName,
                email: additionalSignatory.email,
                title: additionalSignatory.title,
              }
            : undefined,
        }
      : {},
  });

  const isThereAdditionalSignatory = watch('isThereAdditionalSignatory');

  useEffect(() => {
    if (!isThereAdditionalSignatory) {
      setValue('additionalSignatory.firstName', '');
      setValue('additionalSignatory.middleName', '');
      setValue('additionalSignatory.lastName', '');
      setValue('additionalSignatory.email', '');
      setValue('additionalSignatory.title', '');
    }
  }, [isThereAdditionalSignatory, setValue]);

  const onSubmit = async (data: CompanyAuthorizedSignatoriesFormData) => {
    try {
      if (!defaultSignatory) {
        await createMutation({
          firstName: user.basicDetails.firstName,
          middleName: user.basicDetails.middleName,
          lastName: user.basicDetails.lastName,
          email: user.email,
          title: data.title,
          isPrimary: true,
        }).unwrap();
      } else {
        await updateMutation({
          id: defaultSignatory.id,
          title: data.title,
        }).unwrap();
      }

      if (additionalSignatory) {
        if (isThereAdditionalSignatory) {
          await updateMutation({
            ...data.additionalSignatory,
            id: (additionalSignatory as CompanyAuthorizedSignatory).id,
          }).unwrap();
        } else {
          await deleteMutation({
            signatoryId: (additionalSignatory as CompanyAuthorizedSignatory).id,
          }).unwrap();
        }
      } else if (data.additionalSignatory?.firstName) {
        await createMutation({
          ...data.additionalSignatory,
          isPrimary: false,
        }).unwrap();
      }

      await sendCompanyVerificationRequestMutation().unwrap();

      navigate(`/investment-subscription/${productId}/progress`, {
        state: {
          progress: 6,
          type: 'legal_entity',
          completedSteps: [ProgressSteps.Details],
        },
        replace: true,
      });
    } catch (e) {
      console.error(e);
    }
  };

  if (!user) return <></>;

  return (
    <InvestmentContainer data-testid='company-authorized-signatories-container'>
      <ContentContainer>
        <Steps currentStep={3} totalSteps={LEGAL_ENTITY_TOTAL_STEPS} />
        <Text
          variant='h3'
          fontWeight='fontWeightBold'
          sx={{ mt: { xs: 16, md: 5 } }}
          color={theme.palette.black.A100}
        >
          <FormattedMessage id='investment.authorizedSignatories.authorizedSignatories' />
        </Text>
        <Text
          variant='h6'
          mt={5}
          fontWeight='fontWeightMedium'
          color={theme.palette.black[500]}
        >
          <FormattedMessage id='investment.authorizedSignatories.authorizedSignatoriesSubtitle' />
        </Text>

        <Flex mt={16} flexDirection='column' gap={12}>
          <Flex gap={6}>
            <Input
              label={intl.formatMessage({
                id: 'investment.ubo.firstName',
              })}
              inputProps={{
                sx: {
                  textTransform: 'capitalize',
                },
              }}
              disabled
              value={defaultSignatory?.firstName ?? user.basicDetails.firstName}
            />
            <Input
              label={intl.formatMessage({
                id: 'investment.ubo.middleName',
              })}
              inputProps={{
                sx: {
                  textTransform: 'capitalize',
                },
              }}
              disabled
              value={
                defaultSignatory?.middleName ?? user.basicDetails.middleName
              }
            />
            <Input
              label={intl.formatMessage({
                id: 'investment.ubo.lastName',
              })}
              inputProps={{
                sx: {
                  textTransform: 'capitalize',
                },
              }}
              disabled
              value={defaultSignatory?.lastName ?? user.basicDetails.lastName}
            />
          </Flex>
          <Flex flex={1} gap={6}>
            <Input
              label={intl.formatMessage({
                id: 'investment.ubo.email',
              })}
              locked
              value={defaultSignatory?.email ?? user.email}
              sx={{
                flex: 1,
              }}
            />
            <Input
              label={intl.formatMessage({
                id: 'investment.authorizedSignatories.title',
              })}
              {...registerInput(register, 'title')}
              {...getErrorProps(intl, errors.title)}
              sx={{
                flex: 1,
              }}
            />
          </Flex>
          <Flex flexDirection='column' gap={6}>
            <Text
              variant='h6'
              fontWeight='fontWeightSemiBold'
              color={theme.palette.black.A100}
            >
              <FormattedMessage id='investment.authorizedSignatories.isThereAdditionalSignatory' />
            </Text>
            <Controller
              control={control}
              name='isThereAdditionalSignatory'
              render={({ field: { ref, value, ...restProps } }) => (
                <ChoiceInput
                  direction='row'
                  fullWidth
                  choices={yesNoChoices}
                  defaultValues={value !== undefined ? [value] : undefined}
                  {...restProps}
                />
              )}
            />
          </Flex>
          {isThereAdditionalSignatory ? (
            <Flex>
              <Flex flex={1} mt={16} flexDirection='column' gap={12}>
                <Flex gap={6}>
                  <Input
                    label={intl.formatMessage({
                      id: 'investment.ubo.firstName',
                    })}
                    inputProps={{
                      sx: {
                        textTransform: 'capitalize',
                      },
                    }}
                    {...registerInput(
                      register,
                      'additionalSignatory.firstName'
                    )}
                    {...getErrorProps(
                      intl,
                      errors.additionalSignatory?.firstName
                    )}
                  />
                  <Input
                    label={intl.formatMessage({
                      id: 'investment.ubo.middleName',
                    })}
                    inputProps={{
                      sx: {
                        textTransform: 'capitalize',
                      },
                    }}
                    {...registerInput(
                      register,
                      'additionalSignatory.middleName'
                    )}
                    {...getErrorProps(
                      intl,
                      errors.additionalSignatory?.middleName
                    )}
                  />
                  <Input
                    label={intl.formatMessage({
                      id: 'investment.ubo.lastName',
                    })}
                    inputProps={{
                      sx: {
                        textTransform: 'capitalize',
                      },
                    }}
                    {...registerInput(register, 'additionalSignatory.lastName')}
                    {...getErrorProps(
                      intl,
                      errors.additionalSignatory?.lastName
                    )}
                  />
                </Flex>
                <Flex gap={6}>
                  <Input
                    label={intl.formatMessage({
                      id: 'investment.ubo.email',
                    })}
                    {...registerInput(register, 'additionalSignatory.email')}
                    {...getErrorProps(intl, errors.additionalSignatory?.email)}
                    sx={{
                      flex: 1,
                    }}
                  />
                  <Input
                    label={intl.formatMessage({
                      id: 'investment.authorizedSignatories.title',
                    })}
                    {...registerInput(register, 'additionalSignatory.title')}
                    {...getErrorProps(intl, errors.additionalSignatory?.title)}
                    sx={{
                      flex: 1,
                    }}
                  />
                </Flex>
              </Flex>
            </Flex>
          ) : null}
        </Flex>
      </ContentContainer>
      <InvestmentFooter
        secondaryButton={{
          title: intl.formatMessage({
            id: 'misc.previous',
          }),
          onClick: () =>
            navigate(`/investment-subscription/${productId}/progress`, {
              state: {
                progress: 2,
                type: 'legal_entity',
              },
              replace: true,
            }),
        }}
        primaryButton={{
          title: intl.formatMessage({
            id: 'misc.next',
          }),
          disabled: !isValid || isVerificationRequestLoading,
          onClick: handleSubmit(onSubmit),
        }}
      />
    </InvestmentContainer>
  );
};

const CompanyWithProps: React.FC<WithUserProps> = ({ user, refetchUser }) => {
  const { data: signatories, isLoading: isLoadingSignatories } =
    useCompanyAuthorizedSignatoriesQuery();

  if (isLoadingSignatories) return <AppLoader />;
  if (!signatories) return <></>;

  return (
    <CompanyAuthorizedSignatories
      user={user}
      refetchUser={refetchUser}
      signatories={signatories}
    />
  );
};

export default WithUser(CompanyWithProps);
